import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Copy, Send, X } from "lucide-react";
import { toast } from "sonner";

function PageHeader({ overline, title }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="overline text-gold-deep">{overline}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
      <div className="gold-rule mt-4" />
    </div>
  );
}

function splitDraft(text) {
  if (!text) return { subject: "", body: "" };
  const lines = text.split(/\r?\n/);
  const subjLineIdx = lines.findIndex((l) => /^subject\s*:/i.test(l));
  if (subjLineIdx === -1) return { subject: "", body: text.trim() };
  const subject = lines[subjLineIdx].replace(/^subject\s*:\s*/i, "").trim();
  const body = lines.slice(subjLineIdx + 1).join("\n").trim();
  return { subject, body };
}

export default function AIAssistant() {
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [tone, setTone] = useState("professional and warm");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState("");
  const [sendModal, setSendModal] = useState(null); // {to, subject, body}
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data));
  }, []);

  const selectedContact = contacts.find((c) => c.id === contactId);

  const generate = async (e) => {
    e.preventDefault();
    if (!contactId) return toast.error("Pick a contact first.");
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/ai/followup", { contact_id: contactId, tone, context });
      setOutput(data.email);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success("Copied to clipboard");
  };

  const openSend = () => {
    if (!selectedContact?.email) {
      toast.error("This contact has no email on file. Edit the contact and add one.");
      return;
    }
    const { subject, body } = splitDraft(output);
    setSendModal({ to: selectedContact.email, subject: subject || "Follow-up", body });
  };

  const send = async () => {
    setSending(true);
    try {
      await api.post("/email/send", {
        to: sendModal.to,
        subject: sendModal.subject,
        body: sendModal.body,
        contact_id: contactId,
      });
      toast.success(`Email sent to ${sendModal.to}`);
      setSendModal(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader overline="Claude Sonnet 4.5" title="AI Assistant" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={generate} className="bg-white border border-navy/10 p-6 space-y-5" data-testid="ai-form">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-gold-deep" />
            <div className="font-serif text-xl text-navy">Draft follow-up email</div>
          </div>
          <div>
            <label className="overline text-navy/60">Contact</label>
            <select data-testid="ai-contact" value={contactId} onChange={(e) => setContactId(e.target.value)}
              className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
              <option value="">— Select contact —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.organization ? `· ${c.organization}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-navy/60">Tone</label>
            <input data-testid="ai-tone" value={tone} onChange={(e) => setTone(e.target.value)}
              className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="overline text-navy/60">Additional context</label>
            <textarea rows={5} data-testid="ai-context" value={context} onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. Following up on our IC discussion at Africa Investment Forum — want to share updated term sheet."
              className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="ai-generate"
            className="w-full bg-navy text-gold px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-60"
          >
            {loading ? "Drafting…" : "Generate email"}
          </button>
        </form>

        <div className="bg-white border border-navy/10 p-6" data-testid="ai-output-card">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-gold-deep">Draft</div>
            {output && (
              <div className="flex items-center gap-3">
                <button onClick={copy} data-testid="ai-copy" className="text-xs uppercase tracking-widest text-navy/70 hover:text-gold flex items-center gap-2">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
                <button onClick={openSend} data-testid="ai-send" className="text-xs uppercase tracking-widest bg-navy text-gold px-3 py-1.5 hover:bg-navy-600 flex items-center gap-2">
                  <Send className="h-3.5 w-3.5" /> Send
                </button>
              </div>
            )}
          </div>
          {loading && <div className="py-16 text-center text-navy/60 italic">Drafting with Claude Sonnet 4.5…</div>}
          {!loading && !output && (
            <div className="py-16 text-center text-navy/40 italic">Your AI-drafted email will appear here.</div>
          )}
          {output && (
            <pre className="whitespace-pre-wrap font-sans text-sm text-navy/85 leading-relaxed" data-testid="ai-output">{output}</pre>
          )}
        </div>
      </div>

      {sendModal && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="send-modal">
          <div className="bg-white max-w-xl w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold-deep">Resend</div>
                <h2 className="font-serif text-2xl text-navy">Send email</h2>
              </div>
              <button onClick={() => setSendModal(null)} className="text-navy/40 hover:text-navy">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="gold-rule mb-5" />
            <div className="space-y-4">
              <div>
                <label className="overline text-navy/60">To</label>
                <input data-testid="send-to" value={sendModal.to} onChange={(e) => setSendModal({ ...sendModal, to: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Subject</label>
                <input data-testid="send-subject" value={sendModal.subject} onChange={(e) => setSendModal({ ...sendModal, subject: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Body</label>
                <textarea rows={8} data-testid="send-body" value={sendModal.body} onChange={(e) => setSendModal({ ...sendModal, body: e.target.value })}
                  className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm font-mono" />
              </div>
              <div className="text-xs text-navy/50 italic">
                Sent via Resend. If no Resend API key is configured, you'll get an error — add RESEND_API_KEY to backend .env.
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setSendModal(null)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70">Cancel</button>
              <button onClick={send} disabled={sending} data-testid="send-confirm" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-60">
                {sending ? "Sending…" : "Send via Resend"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
